import Phaser from "phaser";
import attachCameraControls from "./cameraControls";
import { socket } from "../socket";

// "Fun Interactions" was renamed "Pop Up Play" in the content
// leave the variable the same for now
const DECK_LABELS = {
    rt:  "Real Talk", suso:"Speak Up", btm: "Behind the Mask", pm:  "Power Moves",
    re:  "Ripple Effect", dtm: "Drop the Mic", scs: "Self Care Squad", dta: "Drop the Anchor",
    fts: "Flip the Script", fi:  "Pop Up Play", default: "Other"
};

const DECK_COLORS = {
    "rt": 0xe63946, 
    "suso": 0xf1a208, 
    "btm": 0x2a9d8,
    "pm": 0x457b9d, 
    "re": 0x264653, 
    "dtm": 0x8d99ae,
    "scs": 0xa7c957, 
    "dta": 0xffb4a2, 
    "fts": 0x6a4c93, 
    "fi": 0xe76f5,
    "default": 0xcccccc 
};

export default class BoardScene extends Phaser.Scene {
    constructor() {
        super("BoardScene");
        // coordinates for each space index, spacePositions[12] = {x, y}
        this.spacePositions = [];
        // map of playerId -> Phaser.GameObjects.Ellipse 
        this.pawns = {};
        // map of playerId -> {x,y} offset 
        this.playerOffsets = {};
        // map of playerId -> current position index
        this.pawnPositions = {};
        // track if a pawn is animating
        this._isAnimating = {};
    }

    preload() {
        // Board background
        // this.load.image("boardBG", "/assets/board.png");

        // Tile images for decks
        const tileIdsWithImages = ["btm", "dta", "dtm", "fts", "pm", "re", "rt", "scs", "suso", "fi"];
        tileIdsWithImages.forEach(id => {
            this.load.image(`${id}_tile`, `/assets/${id}.png`);
        });

        this.load.image("start_tile", "/assets/Start.png");
        this.load.image("end_tile", "/assets/End.png");
    }


    // Called when scene starts. Expects gameState from React
    create(data) {
        this.scene.stop("StartScene");
        const { gameState } = data;
        if (!gameState) return console.error("Game state is missing!");
        
        this.drawBoard(gameState.board);
        this.redrawAllPawns(gameState.players, gameState.turnOrder);
        this.setupCamera(gameState.board);
        this.setupSocketListeners();
    }

    createRoundedRect(x, y, width, height, fillColor, strokeColor, strokeWidth, radius) {
        const g = this.add.graphics({ x: x - width/2, y: y - height/2 });

        // fill
        g.fillStyle(fillColor, 1);
        g.fillRoundedRect(0, 0, width, height, radius);

        // stroke
        if (strokeWidth > 0) {
            g.lineStyle(strokeWidth, strokeColor, 1);
            g.strokeRoundedRect(0, 0, width, height, radius);
        }

        return g;
    }


    // receive board from gameState and draws it onto the screen
    drawBoard(board) {
        const SQUARE_SIZE = 100, SQUARE_MARGIN = 20, COLS = 6;
        const TILE_OFFSET = (SQUARE_SIZE / 2) + 40; // padding

        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;
        
        var row = 0, col = 0;
        var count = 0;
        var direction = 1 //this decides what direction the squares are made; 1 for left to right; -1 for right to left
        board.spaces.forEach(space => {
            const i = space.index; // this square's index
            var x = 0;
            var y = 0;
            if (count < COLS){
                x = col * (SQUARE_SIZE + SQUARE_MARGIN) + TILE_OFFSET;
                y = row * (SQUARE_SIZE + SQUARE_MARGIN) + TILE_OFFSET;
                col += direction;
                count++;
            } else if(count === COLS){
                col += direction*(-1);
                row++;
                x = col * (SQUARE_SIZE + SQUARE_MARGIN) + TILE_OFFSET;
                y = row * (SQUARE_SIZE + SQUARE_MARGIN) + TILE_OFFSET;
                row++;
                count = 0;
                direction *= -1;
            }

            this.spacePositions[i] = { x, y };

            // Update bounds using the full square size
            const half = SQUARE_SIZE / 2;
            minX = Math.min(minX, x - half);
            maxX = Math.max(maxX, x + half);
            minY = Math.min(minY, y - half);
            maxY = Math.max(maxY, y + half);

            // draw square based on deck_id 
            const color = DECK_COLORS[space.deckId] || DECK_COLORS.default;
            const isFirst = i === 0;
            const isLast = i === board.spaces.length - 1;

            if (isFirst || isLast) {
                const specialKey = isFirst ? "start_tile" : "end_tile";

                // optional: a subtle border box behind the image
                this.add.rectangle(x, y, SQUARE_SIZE, SQUARE_SIZE, color)
                    .setStrokeStyle(2, 0x000000)
                    .setDepth(-2);

                this.add.image(x, y, specialKey)
                    .setDisplaySize(SQUARE_SIZE * 0.9, SQUARE_SIZE * 0.9)
                    .setOrigin(0.5)
                    .setDepth(-1);
            } else {
                const tileKey = `${space.deckId}_tile`;
                const hasTileImage = this.textures.exists(tileKey);

                if (hasTileImage) {
                    // rounded colored tile behind
                    this.createRoundedRect(
                        x, y, SQUARE_SIZE, SQUARE_SIZE, color,
                        0x000000, 4, 12 
                    ).setDepth(-2);

                    // tile image
                    this.add.image(x, y, tileKey)
                        .setDisplaySize(SQUARE_SIZE * 0.9, SQUARE_SIZE * 0.9)
                        .setOrigin(0.5)
                        .setDepth(-1);

                } else {
                    // fallback tile with rounded rect
                    this.createRoundedRect(
                        x, y, SQUARE_SIZE, SQUARE_SIZE, color,
                        0x000000, 4, 12
                    ).setDepth(-1);
                }
            }

            // // Square Index Label
            // this.add.text(x, y - 8, i.toString(), {
            //     font: "32px Arial",
            //     color: "#000000",
            //     align: "center"
            // }).setOrigin(0.5);

            // // Deck ID Label
            // const label = (DECK_LABELS[space.deckId] || DECK_LABELS.default);
            // this.add.text(x, y + 18, label, {
            //     font: "12px Arial",
            //     color: "#000000",
            //     align: "center"
            // }).setOrigin(0.5);
        });

        // After all squares are placed, draw the backdrop box
        const padding = 30;

        const boxX = minX - padding;
        const boxY = minY - padding;
        const boxWidth = (maxX - minX) + padding * 2;
        const boxHeight = (maxY - minY) + padding * 2;

        // Semi-transparent blue with a black border, behind tiles
        this.add.rectangle(
            boxX + boxWidth / 2,
            boxY + boxHeight / 2,
            boxWidth,
            boxHeight,
            0xffffff,
            1.0
        )
            .setStrokeStyle(8, 0x000000) // black border
            .setDepth(-5);
    }

    // redraw pawns on game update
    redrawAllPawns(players, turnOrder) {
        // clear all existing pawns and data
        for (const playerId in this.pawns) {
            if (this.pawns[playerId]) this.pawns[playerId].destroy();
        }
        this.pawns = {};
        this.playerOffsets = {};

        // redraw all pawns from the server state
        const PAWN_RADIUS = 20;
        turnOrder.forEach((playerId, index) => {
            const player = players[playerId];
            if (!player) return;

            const coords = this.spacePositions[player.position];
            const offsetX = (index % 2 === 0 ? -1 : 1) * (PAWN_RADIUS / 2);
            const offsetY = (index < 2 ? -1 : 1) * (PAWN_RADIUS / 2);
            this.playerOffsets[playerId] = { x: offsetX, y: offsetY };
            // Initialize pawn position
            this.pawnPositions[playerId] = player.position;

            const pawn = this.add.circle(
                coords.x + offsetX,
                coords.y + offsetY,
                PAWN_RADIUS, 0x000000, 0.8
            ).setStrokeStyle(2, 0xffffff);
            
            this.pawns[playerId] = pawn;
        });
    }

    setupSocketListeners() {
        // Roster changes trigger a full redraw
        socket.on('playerJoined', ({ state }) => {
            console.log('A player joined, redrawing all pawns.');
            this.redrawAllPawns(state.players, state.turnOrder);
        });

        socket.on('playerLeft', ({ state }) => {
            console.log('A player left, redrawing all pawns.');
            this.redrawAllPawns(state.players, state.turnOrder);
        });
        
        // Movement triggers animation
        socket.on('playerMoved', ({ playerId, toPosition }) => {
            this.animatePawn(playerId, toPosition);
        });
    }

    // takes in a pawn and a location, then moves that pawn there
    animatePawn(playerId, toPosition) {
        const pawn = this.pawns[playerId];
        const targetPos = this.spacePositions[toPosition];
        const offset = this.playerOffsets[playerId];

        if (!pawn || !targetPos || !offset) {
            console.error(`Cannot animate pawn ${playerId}, data is missing.`);
            return;
        }

        if (this._isAnimating[playerId]) {
            console.log(`Pawn ${playerId} is already animating; ignoring new request.`);
            return;
        }
        this._isAnimating[playerId] = true;

        const startIndex = this.getCurrentPawnPosition(playerId);
        const endIndex = toPosition;

        if (startIndex === endIndex) {
            this._isAnimating[playerId] = false;
            console.log(`Pawn ${playerId} already at ${toPosition}`);
            return;
        }

        // Build path of indices step-by-step
        const path = [];
        const step = endIndex > startIndex ? 1 : -1;
        for (let idx = startIndex + step; idx !== endIndex + step; idx += step) {
            path.push(idx);
        }

        const durationPerStep = 300; // 300ms per square
        let stepIdx = 0;
        const animateNext = () => {
            if (stepIdx >= path.length) {
                this.updatePawnPosition(playerId, endIndex);
                this._isAnimating[playerId] = false;
                console.log(`Pawn ${playerId} reached final position ${endIndex}`);
                return;
            }

            const idx = path[stepIdx];
            const pos = this.spacePositions[idx];
            const targetX = pos.x + offset.x;
            const targetY = pos.y + offset.y;

            this.tweens.add({
                targets: pawn,
                x: targetX,
                y: targetY,
                duration: durationPerStep,
                ease: 'Power2',
                onComplete: () => {
                    this.updatePawnPosition(playerId, idx);
                    stepIdx += 1;
                    animateNext();
                }
            });
        };

        animateNext();
    }

    // Helper function to get current pawn position (index)
    getCurrentPawnPosition(playerId) {
        return Number.isInteger(this.pawnPositions[playerId]) ? this.pawnPositions[playerId] : 0;
    }

    // Update pawn position when it moves (index)
    updatePawnPosition(playerId, newPosition) {
        this.pawnPositions[playerId] = newPosition;
    }

    // calculate bounds based off size of board
    setupCamera(board) {
        // this.cameras.main.setBackgroundColor("#eee");
        const COLS = 6, SQUARE_SIZE = 100, SQUARE_MARGIN = 20;
        const TILE_OFFSET = (SQUARE_SIZE / 2) + 40;
        var numRows = Math.floor(board.spaces.length / 7) *2;
        if (board.spaces.length % 7 !== 0) {numRows++}
        //console.log(numRows)
        const boardWidth = COLS * (SQUARE_SIZE + SQUARE_MARGIN) + TILE_OFFSET;
        const boardHeight = numRows * (SQUARE_SIZE + SQUARE_MARGIN) + TILE_OFFSET;
        this.controls = attachCameraControls(this, { width: boardWidth, height: boardHeight });
    }

    shutdown() {
        socket.off('playerMoved');
        socket.off('playerJoined');
        socket.off('playerLeft');
    }
}